import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { SeoTags } from "../../components/Head/SeoTags";
import { LogoOnly } from "../../assets/icons/logoOnly";
import TrademarkDisclaimer from "../../components/Legal/TrademarkDisclaimer";

// Bespoke Omnivore page. Omnivore (the read-it-later app) shut down at the end
// of 2024 after being acquired, so this reuses the proven Pocket "shutdown →
// migrate your export → future-proof home" narrative rather than the living-
// competitor template. English-only (localized={false}); listed in
// scripts/routes.js NON_LOCALIZED_ROUTES.

const faqData = [
  {
    question: "Is Omnivore really shut down?",
    answer:
      "Yes. After Omnivore was acquired, the hosted app was discontinued and users were asked to export their data before the service ended. If you saved that export, you can bring it into onBookmarks.",
  },
  {
    question: "I exported my Omnivore data — can I import it?",
    answer:
      "Yes. Omnivore let you export your library as a file of your saved articles. Create a free onBookmarks account and upload it on the import screen; your saves come across with their links and titles.",
  },
  {
    question: "Is onBookmarks free?",
    answer:
      "Yes. The free plan includes unlimited bookmarks, AI tagging, multi-collection organization, offline reading, and browser extensions — no credit card required.",
  },
  {
    question: "Omnivore was open source. Why not self-host?",
    answer:
      "You can — the code is still out there. But self-hosting means running and maintaining your own servers indefinitely. onBookmarks gives you a maintained home with encryption and AI organization so you don't have to babysit infrastructure.",
  },
  {
    question: "Will onBookmarks disappear like Omnivore did?",
    answer:
      "We can't predict the future, but you're never locked in: you can export your entire library from onBookmarks at any time, so your data is always portable.",
  },
  {
    question: "Does onBookmarks keep the read-it-later workflow?",
    answer:
      "Yes. Save now, read later, even offline — plus a reading list to track what you've read. It's the Omnivore habit, with AI organization on top.",
  },
];

const OmnivoreAlternative = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#migration-guide") {
      const element = document.getElementById("migration-guide");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location.hash]);

  return (
    <>
      <SeoTags
        localized={false}
        title="Omnivore Alternative — A New Home After Shutdown | onBookmarks"
        description="Omnivore shut down. onBookmarks is the read-it-later alternative to move to: import your exported Omnivore library, keep offline reading and a clean reading list, and add AI organization and client-side encryption. Free plan available."
        keywords="omnivore alternative, omnivore replacement, omnivore shutdown, omnivore shut down, import omnivore, read it later alternative"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "onBookmarks",
            applicationCategory: "ProductivityApplication",
            operatingSystem: "Web, iOS, Android",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
            alternativeHeadline: "Best Omnivore Alternative After Shutdown",
            description:
              "onBookmarks is a read-it-later and bookmark manager to replace Omnivore after its shutdown: import your export, read offline, and get AI organization and client-side encryption.",
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqData.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          }),
        }}
      />

      {/* Hero */}
      <section className="hero bg-light">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-8 mx-auto text-center">
              <div>
                <div className="d-inline-block rounded-4 bg-danger-subtle fs-normal text-danger px-3 py-2 mb-4">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Omnivore has shut down
                </div>
              </div>
              <h1 className="display-4 fw-bold mb-4">
                A New Home for Your Omnivore Library
              </h1>
              <p className="lead mb-4">
                Omnivore is gone, but your reading list doesn't have to be. If you
                saved your Omnivore export, onBookmarks brings it back to life —
                offline reading, a clean reading list, AI organization, and
                client-side encryption.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-primary btn-lg px-5"
                >
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  Import My Library Free
                </Link>
                <Link
                  to="#migration-guide"
                  className="btn btn-outline-primary btn-lg px-4"
                >
                  See the Migration Guide
                </Link>
              </div>
              <p className="mt-3 text-muted">
                <i className="bi bi-shield-check me-2"></i>
                Free forever plan · No credit card required
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-4 bg-white border-bottom">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-12">
              <div className="d-flex justify-content-center align-items-center gap-5 flex-wrap">
                <div className="text-center">
                  <i className="bi bi-wifi-off h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">Offline reading</small>
                </div>
                <div className="text-center">
                  <i className="bi bi-shield-lock h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">Zero-knowledge encryption</small>
                </div>
                <div className="text-center">
                  <i className="bi bi-robot h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">AI-powered organization</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why onBookmarks */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-4">
              What Omnivore Fans Will Feel at Home With
            </h2>
            <p className="lead text-muted">
              The read-it-later workflow you're used to — on a platform that's
              here for the long haul.
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-warning-subtle rounded-3 mb-3">
                    <i className="bi bi-wifi-off fs-3 text-warning"></i>
                  </div>
                  <h3 className="h5 mb-3">Read Later, Offline</h3>
                  <p className="text-muted mb-3">
                    Save now, read whenever — even without a connection. Your
                    articles are cached on your device.
                  </p>
                  <ul className="list-unstyled small text-muted">
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Offline access to saved articles
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Built-in reading list
                    </li>
                    <li>
                      <i className="bi bi-check text-success me-2"></i>
                      Mark as read to clear the queue
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-primary-subtle rounded-3 mb-3">
                    <i className="bi bi-robot fs-3 text-primary"></i>
                  </div>
                  <h3 className="h5 mb-3">AI Organization</h3>
                  <p className="text-muted mb-3">
                    Omnivore leaned on manual labels. onBookmarks tags and
                    categorizes your saves automatically.
                  </p>
                  <ul className="list-unstyled small text-muted">
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Automatic tag suggestions
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Multi-collection organization
                    </li>
                    <li>
                      <i className="bi bi-check text-success me-2"></i>
                      Full-text search included
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-success-subtle rounded-3 mb-3">
                    <i className="bi bi-shield-lock fs-3 text-success"></i>
                  </div>
                  <h3 className="h5 mb-3">Private &amp; Portable</h3>
                  <p className="text-muted mb-3">
                    Encrypt sensitive saves on your device, and export your whole
                    library whenever you want. No lock-in.
                  </p>
                  <ul className="list-unstyled small text-muted">
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Client-side AES-256 encryption
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      One-click full export, anytime
                    </li>
                    <li>
                      <i className="bi bi-check text-success me-2"></i>
                      Your data stays yours
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Migration Guide */}
      <section id="migration-guide" className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="text-center mb-5">
                <h2 className="display-6 fw-bold mb-4">
                  Bring Your Omnivore Export Over in Three Steps
                </h2>
                <p className="lead text-muted">
                  Already have your export saved? You're minutes away from a fresh
                  home.
                </p>
              </div>

              <div className="card shadow-sm border-0">
                <div className="card-body p-5">
                  <div className="row g-4">
                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px", minWidth: "40px" }}
                        >
                          <span className="fw-bold">1</span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">Locate your Omnivore export</h4>
                          <p className="text-muted mb-0">
                            Omnivore let you download your saved library before it
                            shut down. Find that export file on your computer.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px", minWidth: "40px" }}
                        >
                          <span className="fw-bold">2</span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">Create your free account</h4>
                          <p className="text-muted mb-3">
                            Sign up for onBookmarks — no credit card needed — and
                            open the import screen.
                          </p>
                          <Link
                            to={`${process.env.REACT_APP_APP_URL}/register`}
                            className="btn btn-primary"
                          >
                            Create Free Account
                          </Link>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px", minWidth: "40px" }}
                        >
                          <span className="fw-bold">3</span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">Upload &amp; let AI sort it</h4>
                          <p className="text-muted mb-3">
                            Drop in your file. We import your saves and our AI
                            suggests tags and collections so your library arrives
                            organized.
                          </p>
                          <div className="p-3 rounded-3 bg-success bg-opacity-10 text-success mb-0">
                            <i className="bi bi-check-circle me-2"></i>
                            <span>
                              Duplicate links are flagged during import, so your
                              new library starts clean.
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-center text-muted mt-4 mb-0">
                Not sure about your file?{" "}
                <Link to="/features/import">See the full import guide</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="text-center mb-5">
                <h2 className="display-6 fw-bold mb-4">
                  Omnivore vs. onBookmarks
                </h2>
              </div>

              <div className="table-responsive rounded-3 border shadow-sm">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th scope="col" className="p-3">
                        Feature
                      </th>
                      <th scope="col" className="p-3 text-center bg-light">
                        <i className="bi bi-bookmark-x me-2 text-muted"></i>
                        Omnivore
                      </th>
                      <th
                        scope="col"
                        className="p-3 text-center d-flex align-items-center justify-content-center gap-2"
                      >
                        <LogoOnly width="20" />
                        onBookmarks
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-3 fw-medium">Read later &amp; offline</td>
                      <td className="p-3 text-center bg-light">
                        <span className="text-muted">Was available</span>
                      </td>
                      <td className="p-3 text-center">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">AI-powered organization</td>
                      <td className="p-3 text-center bg-light">
                        <i className="bi bi-x-circle text-danger"></i>
                      </td>
                      <td className="p-3 text-center">
                        <strong className="text-success">Full AI Tagging</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Client-side encryption</td>
                      <td className="p-3 text-center bg-light">
                        <i className="bi bi-x-circle text-danger"></i>
                      </td>
                      <td className="p-3 text-center">
                        <strong className="text-success">Zero-Knowledge</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Multi-collection organization</td>
                      <td className="p-3 text-center bg-light">
                        <span className="text-muted">Labels</span>
                      </td>
                      <td className="p-3 text-center">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Maintained &amp; supported</td>
                      <td className="p-3 text-center text-danger" style={{ backgroundColor: "#fff5f5" }}>
                        <strong>Shut down</strong>
                      </td>
                      <td className="p-3 text-center text-success">
                        <strong>Actively developed</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">No self-hosting required</td>
                      <td className="p-3 text-center bg-light">
                        <span className="text-muted">Self-host to keep it</span>
                      </td>
                      <td className="p-3 text-center">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Free unlimited saves</td>
                      <td className="p-3 text-center bg-light">
                        <span className="text-muted">—</span>
                      </td>
                      <td className="p-3 text-center">
                        <strong className="text-success">Unlimited Free</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h3 className="h2 fw-bold mb-4">Omnivore Alternative FAQ</h3>
          </div>

          <div className="row g-4">
            {faqData.map((faq, index) => (
              <div key={index} className="col-lg-6">
                <div className="h-100">
                  <h5 className="fw-bold mb-3">{faq.question}</h5>
                  <p className="text-muted">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-5 banner-gradient text-white">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h4 className="h1 fw-bold mb-4 text-white">
                Don't Let Your Reading List Die With Omnivore
              </h4>
              <p className="lead mb-4">
                Bring your saved articles into a home that's actively developed,
                private, and yours to leave anytime.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-light btn-lg px-5"
                >
                  <i className="bi bi-rocket-takeoff me-2"></i>
                  Import My Library Free
                </Link>
                <Link
                  to="#migration-guide"
                  className="btn btn-outline-light btn-lg px-4"
                >
                  <i className="bi bi-map me-2"></i>
                  Migration Guide
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrademarkDisclaimer names="Omnivore" />
    </>
  );
};

export default OmnivoreAlternative;
